import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';

export default function AdminNewsletterPage() {
    const [subject, setSubject] = useState('Novidades Incríveis na SkyPlugins! 🚀');
    const [bodyHtml, setBodyHtml] = useState(`<div style="font-size: 24px; color: #0D63F3; font-weight: 600; margin-bottom: 24px;">Olá, {usuario}! 👋</div>
<br>
<p>Temos novidades incríveis esperando por você na nossa loja! Venha conferir os novos plugins premium e atualizações lançadas recentemente para levar o seu servidor de Minecraft ao próximo nível.</p>

<div style="text-align: center; margin: 30px 0;">
    <a href="https://app.skyplugins.com.br" style="display: inline-block; padding: 12px 30px; background-color: #0D63F3; color: #ffffff !important; text-decoration: none; border-radius: 8px; font-weight: 600; box-shadow: 0 4px 12px rgba(13, 99, 243, 0.25);">Acessar Loja</a>
</div>

<p>Esperamos você!</p>
<br>
<p>Atenciosamente,<br><strong>Equipe SkyPlugins</strong></p>`);
    const [recipientsCount, setRecipientsCount] = useState<number | null>(null);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const { showNotification } = useNotificationDispatch();

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const insertHtml = (before: string, after: string = '') => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const selected = text.substring(start, end);
        const replacement = before + selected + after;

        setBodyHtml(text.substring(0, start) + replacement + text.substring(end));

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
        }, 0);
    };

    const handleAddLink = () => {
        const url = prompt('Digite o endereço do link (URL):', 'https://');
        if (url) {
            insertHtml(`<a href="${url}" style="color: #0D63F3; text-decoration: underline;">`, '</a>');
        }
    };

    const handleAddButton = () => {
        const url = prompt('Digite o endereço de destino (URL) do botão:', 'https://app.skyplugins.com.br');
        const text = prompt('Digite o texto do botão:', 'Conferir');
        if (url && text) {
            const buttonHtml = `\n<div style="text-align: center; margin: 30px 0;">\n    <a href="${url}" style="display: inline-block; padding: 12px 30px; background-color: #0D63F3; color: #ffffff !important; text-decoration: none; border-radius: 8px; font-weight: 600; box-shadow: 0 4px 12px rgba(13, 99, 243, 0.25);">${text}</a>\n</div>\n`;
            insertHtml(buttonHtml);
        }
    };

    // Status do envio
    const [status, setStatus] = useState({
        running: false,
        total: 0,
        sent: 0,
        failed: 0,
        errors: [] as string[],
        cancelRequested: false
    });

    const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Buscar quantidade de inscritos
    useEffect(() => {
        const fetchRecipientsCount = async () => {
            try {
                const response = await apiClient.get('/admin/newsletter/recipients-count');
                setRecipientsCount(response.data.count);
            } catch (err: any) {
                console.error(err);
                showNotification('Falha ao obter total de inscritos.', 'error');
            }
        };

        const fetchStatus = async () => {
            try {
                const response = await apiClient.get('/admin/newsletter/status');
                setStatus(response.data);
                if (response.data.running) {
                    setSending(true);
                    startPollingStatus();
                }
            } catch (err) {
                console.error(err);
            }
        };

        fetchRecipientsCount();
        fetchStatus();

        return () => {
            if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showNotification]);

    const startPollingStatus = () => {
        if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);

        statusIntervalRef.current = setInterval(async () => {
            try {
                const response = await apiClient.get('/admin/newsletter/status');
                setStatus(response.data);
                if (!response.data.running) {
                    setSending(false);
                    if (statusIntervalRef.current) {
                        clearInterval(statusIntervalRef.current);
                        statusIntervalRef.current = null;
                    }
                    showNotification(`Envio de newsletter concluído! Enviado com sucesso para ${response.data.sent} de ${response.data.total} usuários.`, 'success');
                }
            } catch (err) {
                console.error('Erro ao buscar status do envio:', err);
            }
        }, 1500);
    };

    const handlePreview = async () => {
        if (!bodyHtml) {
            showNotification('Escreva o corpo do e-mail para visualizar o preview.', 'error');
            return;
        }

        setPreviewLoading(true);
        try {
            const response = await apiClient.post('/admin/newsletter/preview', { bodyHtml });
            setPreviewHtml(response.data.previewHtml);
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Erro ao gerar preview.', 'error');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSend = async () => {
        if (!subject || !bodyHtml) {
            showNotification('Assunto e corpo do e-mail são obrigatórios.', 'error');
            return;
        }

        if (!window.confirm(`Você tem certeza que deseja iniciar o envio desta newsletter para ${recipientsCount ?? 0} usuários inscritos?`)) {
            return;
        }

        setSending(true);
        try {
            const response = await apiClient.post('/admin/newsletter/send', { subject, bodyHtml });
            showNotification(response.data.message || 'Envio iniciado com sucesso!', 'success');
            setStatus(prev => ({
                ...prev,
                running: true,
                total: response.data.total,
                sent: 0,
                failed: 0,
                errors: [],
                cancelRequested: false
            }));
            startPollingStatus();
        } catch (err: any) {
            setSending(false);
            showNotification(err.response?.data?.error || 'Falha ao iniciar envio.', 'error');
        }
    };

    const handleStop = async () => {
        try {
            const response = await apiClient.post('/admin/newsletter/stop');
            showNotification(response.data.message, 'info');
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Erro ao solicitar parada.', 'error');
        }
    };

    const progressPercent = status.total > 0
        ? Math.round(((status.sent + status.failed) / status.total) * 100)
        : 0;

    return (
        <div className="space-y-8 max-w-7xl mx-auto">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">E-mail Newsletter</h1>
                <p className="mt-2 text-slate-600">
                    Crie, configure e envie boletins informativos e novidades por e-mail para os usuários inscritos.
                </p>
            </div>

            {/* Status Panel (Se estiver rodando) */}
            {(status.running || status.sent + status.failed > 0) && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <span className={`h-2.5 w-2.5 rounded-full ${status.running ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}></span>
                                {status.running ? 'Envio em Andamento' : 'Envio Finalizado'}
                            </h2>
                            <p className="text-sm text-slate-500 mt-1">
                                {status.running ? 'Os e-mails estão sendo enviados um a um com intervalo de 3 segundos.' : 'O envio foi concluído ou interrompido.'}
                            </p>
                        </div>
                        {status.running && (
                            <button
                                onClick={handleStop}
                                disabled={status.cancelRequested}
                                className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-50 text-sm font-semibold rounded-lg border border-red-200 transition-colors"
                            >
                                {status.cancelRequested ? 'Parando...' : 'Parar Envio'}
                            </button>
                        )}
                    </div>

                    {/* Progress bar */}
                    <div className="space-y-2">
                        <div className="flex justify-between text-sm font-semibold text-slate-700">
                            <span>Progresso</span>
                            <span>{progressPercent}% ({status.sent + status.failed} / {status.total})</span>
                        </div>
                        <div className="w-full bg-slate-100 h-3.5 rounded-full overflow-hidden">
                            <div
                                className="bg-blue-600 h-full transition-all duration-500 ease-out"
                                style={{ width: `${progressPercent}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Grid de estatísticas */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-slate-50 p-4 rounded-xl text-center">
                            <span className="block text-xs font-semibold text-slate-500 uppercase">Enviados</span>
                            <span className="text-2xl font-bold text-slate-800">{status.sent}</span>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-xl text-center">
                            <span className="block text-xs font-semibold text-slate-500 uppercase">Falhas</span>
                            <span className="text-2xl font-bold text-red-600">{status.failed}</span>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-xl text-center">
                            <span className="block text-xs font-semibold text-slate-500 uppercase">Total Geral</span>
                            <span className="text-2xl font-bold text-slate-800">{status.total}</span>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-xl text-center">
                            <span className="block text-xs font-semibold text-slate-500 uppercase">Status</span>
                            <span className={`text-sm font-bold inline-block px-2 py-0.5 rounded-full mt-1.5 ${status.running ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}`}>
                                {status.running ? 'Executando' : 'Inativo'}
                            </span>
                        </div>
                    </div>

                    {/* Exibir logs/erros se houver */}
                    {status.errors.length > 0 && (
                        <div className="bg-red-50/50 rounded-xl p-4 border border-red-100 max-h-48 overflow-y-auto">
                            <h3 className="text-sm font-semibold text-red-800 mb-2">Logs de Erros:</h3>
                            <ul className="text-xs text-red-700 space-y-1 font-mono">
                                {status.errors.slice(-20).map((err, idx) => (
                                    <li key={idx}>- {err}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Form & Preview Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Form Card */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Nova Mensagem</h2>
                        <p className="text-slate-500 text-sm mt-1">Escreva o conteúdo HTML da newsletter.</p>
                    </div>

                    <div className="space-y-4">
                        {/* Assunto */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">
                                Assunto do E-mail
                            </label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                disabled={sending}
                                placeholder="ex: Super Promoção de Férias! 🚀"
                                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all text-slate-800"
                            />
                        </div>

                        {/* Corpo HTML */}
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm font-semibold text-slate-700">
                                    Conteúdo em HTML
                                </label>
                                <span className="text-xs text-slate-400">Suporta tags HTML como &lt;p&gt;, &lt;b&gt;, &lt;a&gt;, etc.</span>
                            </div>

                            {/* Barra de Ferramentas de Formatação */}
                            <div className="flex flex-wrap gap-1 mb-2 p-1.5 bg-slate-50 border border-slate-200 rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => insertHtml('<b>', '</b>')}
                                    className="px-2.5 py-1 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 rounded border border-slate-200 shadow-sm transition-all"
                                    title="Negrito"
                                >
                                    Negrito (B)
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertHtml('<i>', '</i>')}
                                    className="px-2.5 py-1 text-xs italic text-slate-700 bg-white hover:bg-slate-100 rounded border border-slate-200 shadow-sm transition-all"
                                    title="Itálico"
                                >
                                    Itálico (I)
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertHtml('<p>', '</p>')}
                                    className="px-2.5 py-1 text-xs text-slate-700 bg-white hover:bg-slate-100 rounded border border-slate-200 shadow-sm transition-all"
                                    title="Parágrafo"
                                >
                                    Parágrafo (P)
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertHtml('<ul>\n  <li>', '</li>\n</ul>')}
                                    className="px-2.5 py-1 text-xs text-slate-700 bg-white hover:bg-slate-100 rounded border border-slate-200 shadow-sm transition-all"
                                    title="Lista com Marcadores"
                                >
                                    Marcadores (•)
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAddLink}
                                    className="px-2.5 py-1 text-xs text-blue-600 bg-white hover:bg-slate-100 rounded border border-slate-200 shadow-sm transition-all font-semibold"
                                    title="Inserir Link"
                                >
                                    Inserir Link (URL)
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAddButton}
                                    className="px-2.5 py-1 text-xs text-indigo-600 bg-white hover:bg-slate-100 rounded border border-slate-200 shadow-sm transition-all font-semibold"
                                    title="Inserir Botão de Ação"
                                >
                                    Inserir Botão
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertHtml('{usuario}')}
                                    className="px-2.5 py-1 text-xs text-emerald-600 bg-white hover:bg-emerald-50 rounded border border-emerald-200 shadow-sm transition-all font-mono font-semibold"
                                    title="Placeholder Nome de Usuário"
                                >
                                    {'{usuario}'}
                                </button>
                            </div>

                            <textarea
                                ref={textareaRef}
                                value={bodyHtml}
                                onChange={(e) => setBodyHtml(e.target.value)}
                                disabled={sending}
                                rows={12}
                                placeholder="<p>Olá!</p><p>Temos ótimas novidades no nosso site hoje...</p>"
                                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all text-slate-800 font-mono text-sm"
                            />
                        </div>

                        <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 text-xs text-blue-700 space-y-1">
                            <p className="font-semibold">💡 Dicas e Lembretes:</p>
                            <p>- O rodapé padrão com o link para <b>cancelar inscrição</b> será incluído automaticamente ao final do e-mail.</p>
                            <p>- Você pode pré-visualizar o e-mail completo no painel ao lado.</p>
                            <p>- O envio possui um limitador de taxa (3 segundos por usuário) para evitar que o servidor SMTP sofra bloqueio ou seja classificado como SPAM.</p>
                        </div>

                        {/* Action buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <button
                                onClick={handlePreview}
                                disabled={previewLoading}
                                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-sm transition-all"
                            >
                                {previewLoading ? 'Gerando Preview...' : 'Ver Preview'}
                            </button>
                            <button
                                onClick={handleSend}
                                disabled={sending || !subject || !bodyHtml}
                                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm disabled:opacity-50 transition-all shadow-sm"
                            >
                                {sending ? 'Enviando...' : `Enviar para ${recipientsCount !== null ? `${recipientsCount} inscritos` : 'inscritos'}`}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Preview Card */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col min-h-[500px]">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Visualização do E-mail</h2>
                        <p className="text-slate-500 text-sm mt-1">Como seu cliente visualizará o e-mail na caixa de entrada.</p>
                    </div>

                    <div className="mt-4 flex-1 border border-slate-200 rounded-xl overflow-hidden bg-slate-50 flex items-center justify-center min-h-[400px]">
                        {previewHtml ? (
                            <iframe
                                title="email-preview"
                                srcDoc={previewHtml}
                                className="w-full h-full border-0 bg-white"
                            />
                        ) : (
                            <div className="text-center p-6 text-slate-400">
                                <svg className="mx-auto h-12 w-12 text-slate-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>Clique em "Ver Preview" para visualizar seu e-mail formatado aqui.</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
